import Image from 'next/image'
import { getReadableImageUrl } from '@/lib/directus'
import { sanitizeHTML } from '@/lib/utils'
import { useEffect, useRef, useState } from 'react'

interface FeaturedLogosSectionData {
	heading?: {
		title?: string
	}
	
	images?: {
		images_id?: {
			alt?: string
			image_file?: {
				image?: {
					id: string
					filename_download: string
				}
			}
		}
	}[]

	image?: {
		alt?: string
		image_file?: {
			image?: {
				id: string
				filename_download: string
			}
		}
	}
}

export const fields = [
	'heading.title',
	'images.images_id.alt',
	'images.images_id.image_file.image.id',
	'images.images_id.image_file.image.filename_download',
	'image.alt',
	'image.image_file.image.id',
	'image.image_file.image.filename_download',
] as const

interface FeaturedLogosSectionProps {
	data?: FeaturedLogosSectionData
}

export default function FeaturedLogosSection({
	data,
}: FeaturedLogosSectionProps) {
	const sectionRef = useRef<HTMLElement>(null)
	const [isVisible, setIsVisible] = useState(false)

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				setIsVisible(entry.isIntersecting)
			},
			{ threshold: 0.15, rootMargin: '-50px' }
		)

		const currentSection = sectionRef.current

		if (currentSection) {
			observer.observe(currentSection)
		}

		return () => {
			if (currentSection) {
				observer.unobserve(currentSection)
			}
		}
	}, [])

	const logos = data?.images || []
	const singleImage = data?.image

	
	if (singleImage?.image_file?.image?.id) {
		return (
			<section id='BrandsSection' className='bg-[#f9fafa]'>
				<Image
					src={getReadableImageUrl(
						singleImage.image_file.image.id,
						singleImage.image_file.image.filename_download || ''
					)}
					alt={singleImage.alt || 'Featured Logos'}
					width={1920}
					height={1080}
					className='py-[27px] w-[90%] max-w-[1080px] mx-auto'
					quality={95}
					loading='lazy'
				/>
			</section>
		)
	}

	
	return (
		<section
			ref={sectionRef}
			id='BrandsSection'
			className='py-10 md:py-16 bg-[#f9fafa]'
		>
			<div className='container'>
				<div className='flex flex-col lg:flex-row justify-between items-start md:gap-10'>
					{/* Title */}
					<div
						className={`w-full lg:w-[250px] transition-all duration-700 ease-out ${
							isVisible
								? 'opacity-100 translate-x-0'
								: 'opacity-0 -translate-x-8'
						}`}
					>
						{data?.heading?.title ? (
							<h2
								className='text-center lg:text-left md:m-0'
								dangerouslySetInnerHTML={sanitizeHTML(data.heading.title)}
							/>
						) : null}
					</div>

					{/* Logos Grid */}
					<div className='flex-1 w-full lg:w-auto'>
						{logos.length > 0 && (
							<div className='flex flex-wrap items-center justify-center 2xl:justify-between gap-8 2xl:gap-6'>
								{logos.map((logo, index) => (
									<div
										key={index}
										className={`transition-all duration-700 ease-out grayscale hover:grayscale-0 ${
											isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
										}`}
										style={{
											transitionDelay: isVisible ? `${index * 100}ms` : '0ms',
										}}
									>
										<Image
											src={getReadableImageUrl(
												logo?.images_id?.image_file?.image?.id || '',
												logo?.images_id?.image_file?.image?.filename_download ||
													''
											)}
											alt={logo?.images_id?.alt || ''}
											width={140}
											height={60}
											className='h-8 md:h-10 lg:h-12 w-auto object-contain'
											quality={90}
											loading='lazy'
										/>
									</div>
								))}
							</div>
						)}
					</div>
				</div>
			</div>
		</section>
	)
}
